/**
 * The cards a chest deals, as DOM, in Blockari's structure (apps/client/src/screens/ChestScreen.tsx `RewardCard` and
 * styles.css `.chest-card`): a slot-sized card with the reward's icon, one big value and a one-word label, a cosmetic
 * with its name and a NEW tag. It leaves the chest's mouth on an arc, grows into its slot, and lands with one glint.
 * Nothing else: no flip, no hero beat, no foil, no float, no count-up.
 *
 * 21 Wild's own are the theme only (ui.css): the card's stock and edge, a suit pip in the corner like a playing
 * card's index, the table's gold, felt and violet where Blockari uses its amber, mint and indigo, and 21 Wild's own
 * reward art (public/chest-art) for the icons.
 *
 * One card is a pure function of the stage clock (`paintReward`): a parked capture steps it frame for frame, and each
 * write is a `transform` or an `opacity` on a layer the compositor already holds.
 */

import { cosmetic, type ChestItem, type Rarity } from '@/game/chests';
import { el } from '@/ui/hud';

import { clamp01, easeOutBack, easeOutCubic } from './chest-timeline';

export type ArtKey = 'coin-stack' | 'ticket' | 'boost' | 'frame' | 'title';

/** What a card says (Blockari's `RewardCard`): the value in big type, a one-word label, a cosmetic's name. */
export interface RewardCopy {
  /** The icon. */
  readonly art: ArtKey;
  /** The big line: "+45", "100", "8" (a boost's games), or a cosmetic's name. */
  readonly value: string;
  /** The small line under it: "$CHAIN", "TICKET", "8 GAMES", "FRAME", "TITLE". */
  readonly label: string;
  /** A boost's "2×": the glyph over its icon, with "XP" under it (Blockari's `.boost-glyph`). */
  readonly glyph?: string;
  /** A cosmetic's own rarity (its face is struck in it), or null: the other cards have one face each. */
  readonly rarity: Rarity | null;
}

export const RARITY_LABEL: Readonly<Record<Rarity, string>> = {
  common: 'COMMON',
  rare: 'RARE',
  epic: 'EPIC',
  legendary: 'LEGENDARY',
};

export function copyOf(item: ChestItem): RewardCopy {
  switch (item.type) {
    case 'chain':
      return { art: 'coin-stack', value: `+${item.amount}`, label: '$CHAIN', rarity: null };
    case 'ticket':
      return { art: 'ticket', value: String(item.fee), label: 'TICKET', rarity: null };
    case 'boost':
      return { art: 'boost', glyph: `${item.multiplier}×`, value: String(item.games), label: 'GAMES', rarity: null };
    case 'cosmetic': {
      const c = cosmetic(item.id);
      return {
        art: item.kind === 'frame' ? 'frame' : 'title',
        value: c?.name ?? item.id,
        label: item.kind === 'frame' ? 'FRAME' : 'TITLE',
        rarity: c?.rarity ?? item.rarity,
      };
    }
  }
}

/** The art's URL (public/chest-art). */
export const artUrl = (key: ArtKey): string => `${import.meta.env.BASE_URL}chest-art/${key}.webp`;

/** Starts every art file loading and decoding (so no card ever waits on one). Resolves when all settled. */
export function preloadArt(keys: readonly ArtKey[]): Promise<void> {
  return Promise.all(
    [...new Set(keys)].filter((key) => key !== 'boost').map(
      (key) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.src = artUrl(key);
          const done = (): void => resolve();
          if (typeof img.decode === 'function') img.decode().then(done, done);
          else img.onload = img.onerror = done;
        }),
    ),
  ).then(() => undefined);
}

/** Words longer than a card can hold at a readable size break here (a soft hyphen: invisible unless used). */
const LONG_WORD = 9;
export function softBreak(word: string): string {
  if (word.length <= LONG_WORD) return word;
  // Compounds break at their join ("House-breaker"); anything else near the middle.
  const joins = ['breaker', 'master', 'keeper', 'hunter', 'stone', 'smith'];
  const lower = word.toLowerCase();
  const j = joins.find((s) => lower.endsWith(s) && lower.length - s.length >= 3);
  const at = j ? word.length - j.length : Math.ceil(word.length / 2);
  return `${word.slice(0, at)}­${word.slice(at)}`;
}

/**
 * A cosmetic's name, never wider than its card: CSS sizes it from the longest word, then a measurement once the
 * display face has loaded shrinks it further if that word still overflows (Blockari's `FitName`).
 */
function fitName(name: HTMLElement): void {
  const fit = (): void => {
    name.style.fontSize = '';
    const box = name.clientWidth;
    if (box > 0 && name.scrollWidth > box) {
      const px = parseFloat(getComputedStyle(name).fontSize);
      name.style.fontSize = `${Math.max(10, Math.floor(px * (box / name.scrollWidth) * 10) / 10)}px`;
    }
  };
  fit();
  void document.fonts?.ready.then(fit);
}

export interface RewardCard {
  /** In the slot: the card. */
  readonly card: HTMLElement;
  readonly copy: RewardCopy;
  /** The last string written to each animated style (see `put`): a frame writes only what changed. */
  memo: Record<string, string>;
  /** Whether the card is on show (a card that has not left the chest is written once, at a whisper). */
  shown: boolean;
  /** Whether it has landed (the glint class is added once). */
  landed: boolean;
}

/** Builds one card for `slot` and appends it. */
export function mountReward(slot: HTMLElement, item: ChestItem): RewardCard {
  const copy = copyOf(item);
  const cosmeticItem = item.type === 'cosmetic';
  const card = el('div', `w21-chest-card t-${item.type}${cosmeticItem ? ` k-${item.kind} r-${copy.rarity}` : ''}`);
  card.dataset.art = copy.art;
  // Not at 0: an element at opacity 0 is skipped by the compositor, and its layer would be rastered on the frame it
  // first shows. At 0.001 it is painted (and invisible) from the moment it is built.
  card.style.opacity = '0.001';

  if (cosmeticItem) card.append(el('i', 'cc-new', 'NEW'));
  const icon = el('span', 'cc-icon');
  // A boost's icon is Blockari's "2x / XP" glyph alone (the corner heart carries the theme); every other card has art.
  if (copy.glyph === undefined) {
    const img = el('img', 'cc-art') as HTMLImageElement;
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    img.src = artUrl(copy.art);
    icon.append(img);
  } else {
    const g = el('span', 'boost-glyph', copy.glyph);
    g.append(el('small', undefined, 'XP'));
    icon.append(g);
  }
  card.append(icon);

  let name: HTMLElement | null = null;
  if (cosmeticItem) {
    const shown = copy.value.split(/\s+/).map(softBreak).join(' ');
    const word = Math.max(...shown.split(/[\s­]+/).map((w) => w.length));
    name = el('b', 'cc-name', shown);
    name.setAttribute('lang', 'en');
    name.style.setProperty('--word', String(word));
    card.append(name);
  } else {
    card.append(el('b', undefined, copy.value));
  }
  card.append(el('small', undefined, copy.label));
  slot.append(card);
  // Measured once here (the cards are built before the clock starts), never in a frame.
  if (name) fitName(name);
  return { card, copy, memo: {}, shown: false, landed: false };
}

/** A card's state at one frame of the stage's clock. */
export interface RewardPaint {
  /** 0..1 through the flight out of the chest into its slot (reduced motion: through the fade). */
  readonly u: number;
  /** Whether the card has left the chest at all. */
  readonly started: boolean;
  /** The mouth of the chest, from the slot's centre, in px. */
  readonly from: { readonly x: number; readonly y: number };
  /** The slot's height in px (the arc rises 0.9 of it). */
  readonly height: number;
  /** 0..1 through COLLECT's lift and fade (already staggered per card). */
  readonly collect: number;
  readonly reduced: boolean;
}

const f2 = (n: number): string => n.toFixed(2);
const f3 = (n: number): string => n.toFixed(3);

/** Writes one animated style only when its string changed (every inline-style mutation is a style recalc). */
function put(c: RewardCard, key: string, prop: 'opacity' | 'transform', v: string): void {
  if (c.memo[key] === v) return;
  c.memo[key] = v;
  c.card.style[prop] = v;
}

/** Writes one frame: a transform and an opacity (Blockari's `paint`, cards loop). */
export function paintReward(c: RewardCard, p: RewardPaint): void {
  const { card } = c;
  if (!p.started) {
    if (c.shown) {
      card.style.opacity = '0.001';
      c.memo = {};
      c.shown = false;
    }
    return;
  }
  c.shown = true;
  const collect = p.collect;
  if (p.reduced) {
    // A short fade, in place: nothing flies.
    put(c, 'o', 'opacity', f3(clamp01(p.u) * (1 - collect)));
    put(c, 't', 'transform', 'none');
    return;
  }
  // Out of the mouth on an arc into the slot (Blockari: easeOutCubic along the line, a sine's rise over it, and the
  // size 0.25 to 1 on easeOutBack(1.6)).
  const u = p.u;
  const e = easeOutCubic(u);
  const dx = p.from.x * (1 - e);
  const dy = p.from.y * (1 - e) - Math.sin(Math.PI * u) * p.height * 0.9;
  const s = 0.25 + 0.75 * easeOutBack(u, 1.6);
  let out = '';
  let alpha = clamp01(u * 4);
  if (collect > 0) {
    // Collected: the card lifts and fades as its reward flies to the top bar.
    out = ` translateY(${f2(-24 * easeOutCubic(collect))}px)`;
    alpha *= 1 - collect;
  }
  put(c, 'o', 'opacity', f3(alpha));
  put(c, 't', 'transform', `translate(${f2(dx)}px, ${f2(dy)}px) scale(${s.toFixed(4)})${out}`);
  if (u >= 1 && !c.landed) {
    c.landed = true;
    // The card lands with a glint where it sits (the stage adds the sparks).
    card.classList.add('landed');
  }
}

/** A card back in the chest's mouth (a replay of the same roll): not landed, not shown, not written. */
export function resetReward(c: RewardCard): void {
  c.card.classList.remove('landed');
  c.card.style.opacity = '0.001';
  c.memo = {};
  c.shown = false;
  c.landed = false;
}
